import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Sun, Moon, LogOut, Search as SearchIcon, History, Users, MessageSquare, Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", label: "Recherche", icon: SearchIcon, testid: "nav-search" },
  { to: "/recherches", label: "Historique", icon: History, testid: "nav-history" },
  { to: "/prospects", label: "Prospects", icon: Users, testid: "nav-prospects" },
  { to: "/modeles-sms", label: "Modeles SMS", icon: MessageSquare, testid: "nav-sms-templates" },
  { to: "/coefficients", label: "Coefficients", icon: Gauge, testid: "nav-commercial-values" },
];

export function Layout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-8">
            <span className="font-heading text-xl font-black tracking-tighter uppercase" data-testid="app-title">
              Lead Finder
            </span>
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 h-16 border-b-2 font-mono text-xs uppercase tracking-wider transition-colors duration-75 ${
                      isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  <item.icon size={14} /> {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline" data-testid="current-user-email">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-none"
              data-testid="theme-toggle-button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </Button>
            <Button variant="ghost" size="icon" className="rounded-none" data-testid="logout-button" onClick={handleLogout}>
              <LogOut size={16} />
            </Button>
          </div>
        </div>
        <nav className="flex md:hidden items-center gap-1 border-t border-border">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-1 px-3 h-10 border-b-2 font-mono text-xs uppercase tracking-wider ${
                  isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground"
                }`
              }
            >
              <item.icon size={12} /> {item.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
